import colors from './colors';
import fonts from './fonts';
import shadows from './shadows';
import sizes from './sizes';

const { fixedSpacing, radius, font } = sizes;

/**
 * Chart theme generator function
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {object} Chart theme object
 */
// @ts-ignore
const chartTheme = ({ darkMode, hdMonitor }) => ({
    backgroundColor: darkMode ? colors.coolGray800 : colors.white,
    boxShadow: darkMode ? shadows.dark.sm : shadows.light.sm,
    viewPadding: 0,
    viewPaddingMore: fixedSpacing.sm,
    selectedItemBorderColor: colors.primary500,
    spacing: [fixedSpacing.lg, fixedSpacing.lg, fixedSpacing.lg, fixedSpacing.lg],
    margin: [0, fixedSpacing['2xl'], 0, fixedSpacing['2xl']],
    maxWidth: 600,
    padding: fixedSpacing.lg,
    legacyGridLineWidth: 0,

    // Chart data colors (10 colors for pie/bar charts)
    dataColors: darkMode
        ? [
              colors.primary500, // #38A2FB
              colors.primary300, // #97D0FF
              colors.primary200, // #C1E3FF
              colors.green400, // #50D7A8
              colors.green300, // #7AEAC3
              colors.green200, // #98F6D6
              colors.orange500, // #FD911E
              colors.orange400, // #FFAB52
              colors.orange200, // #FFDEAB
              colors.coolGray500 // #68777A
          ]
        : [
              colors.primary600, // #1078D0
              colors.primary500, // #38A2FB
              colors.primary300, // #97D0FF
              colors.green500, // #2CBB94
              colors.green400, // #50D7A8
              colors.green200, // #98F6D6
              colors.orange600, // #D97812
              colors.orange500, // #FD911E
              colors.orange300, // #FFCC7E
              colors.coolGray300 // #D0D9DB
          ],

    card: {
        backgroundColor: darkMode ? colors.coolGray800 : colors.white,
        borderRadius: radius.xl, // 12px
        boxShadow: darkMode ? shadows.dark.sm : shadows.light.sm,
        paddingVertical: fixedSpacing['4xl'], // 32px
        paddingHorizontal: fixedSpacing['3xl'], // 24px
        headerPadding: `${fixedSpacing['2xl']}px ${fixedSpacing['3xl']}px ${fixedSpacing.md}px ${fixedSpacing['3xl']}px`
    },

    header: {
        font: fonts.text.lg.semiBold,
        padding: `${fixedSpacing['2xl']}px ${fixedSpacing['3xl']}px ${fixedSpacing.md}px ${fixedSpacing['3xl']}px`,
        gap: fixedSpacing.xl,
        titleMargin: 0,
        title: {
            ...fonts.text.lg.semiBold, // 18px Inter SemiBold
            color: darkMode ? colors.coolGray50 : colors.coolGray800,
            lineHeight: 28
        }
    },

    legend: {
        insideMarginTop: 2,
        insideLabelFont: fonts.text.xs.regular,
        labelFont: { ...fonts.text.sm.regular, fontSize: hdMonitor ? 14 : 13 }, // 14px/12px Inter Medium
        valueFont: { ...fonts.text.sm.regular, fontSize: hdMonitor ? 14 : 13 }, // 14px/12px Inter Bold
        color: darkMode ? colors.coolGray200 : colors.coolGray700,
        dotSize: fixedSpacing.lg, // 12px
        dotRadius: fixedSpacing.sm, // 6px
        dotPadding: fixedSpacing.md, // 8px
        itemSpacing: fixedSpacing.xs, // 4px between legend items
        itemGap: 10, // gap between dot and text
        lineHeight: hdMonitor ? fixedSpacing['2xl'] : 18, // 20/18
        legendHoverColor: darkMode ? colors.coolGray200 : colors.coolGray700,
        disabledOpacity: 0.3,
        hoverOpacity: 0.7
    },

    tooltip: {
        backgroundColor: darkMode ? colors.coolGray900 : colors.white,
        borderColor: colors.coolGray600, // #4F5A5C
        borderWidth: 1,
        borderRadius: radius.xs, // 4px
        titleFont: fonts.text.xs.semiBold, // 12px Inter SemiBold
        bodyFont: fonts.text.xs.regular, // 12px Inter Regular
        titleColor: colors.coolGray100,
        bodyColor: darkMode ? colors.coolGray200 : colors.coolGray700,
        boxShadow: darkMode ? shadows.dark.lg : shadows.light.lg,
        padding: fixedSpacing.sm // 6px
    },

    totals: {
        gap: fixedSpacing.md, // 8px
        padding: `${fixedSpacing['4xl']}px ${fixedSpacing['3xl'] + fixedSpacing.xl}px`, //  32px 40px
        count: {
            fontFamily: '"Inter Tight", Inter, sans-serif',
            fontWeight: fonts.display.xl.semiBold.fontWeight,
            lineHeight: hdMonitor ? 90 : 72,
            fontSize: hdMonitor ? 72 : 72,
            color: darkMode ? colors.coolGray25 : colors.coolGray800,
            letterSpacing: hdMonitor ? -1.44 : -1.2
        },
        title: {
            fontFamily: '"Inter Tight", Inter, sans-serif',
            fontWeight: fonts.text.xs.semiBold.fontWeight,
            lineHeight: hdMonitor ? fixedSpacing['3xl'] : fixedSpacing['2xl'],
            fontSize: hdMonitor ? font.md : font.sm,
            color: darkMode ? colors.coolGray200 : colors.coolGray700,
            defaultText: 'Total'
        },
        // Multi-item list layout
        list: {
            columnGap: fixedSpacing['3xl'], // 24px between columns
            rowGap: fixedSpacing.xxs, // 2px between rows
            paddingHorizontal: fixedSpacing['3xl'], // 24px
            paddingVertical: fixedSpacing['4xl'], // 32px
            item: {
                paddingHorizontal: fixedSpacing.lg, // 12px
                paddingVertical: fixedSpacing.sm, // 6px
                borderRadius: fixedSpacing.sm, // 6px
                backgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
                title: {
                    fontFamily: 'Inter',
                    fontSize: font.sm, // 14px
                    fontWeight: fonts.text.sm.semiBold.fontWeight,
                    lineHeight: fixedSpacing['2xl'],
                    color: darkMode ? colors.coolGray300 : colors.coolGray600
                },
                count: {
                    fontFamily: '"Inter Tight", Inter, sans-serif',
                    fontSize: font['2xl'], // 24px
                    fontWeight: fonts.display.sm.semiBold.fontWeight,
                    lineHeight: fixedSpacing['4xl'],
                    color: darkMode ? colors.coolGray200 : colors.coolGray700
                }
            }
        }
    },

    axes: {
        labelFont: { ...fonts.text.sm.regular, fontSize: hdMonitor ? 14 : 13 }, // 14px/13px Inter Medium
        labelColor: darkMode ? colors.coolGray400 : colors.coolGray600,
        gridLineColor: darkMode ? colors.coolGray700 : colors.coolGray200,
        gridLineWidth: 1
    },

    bar: {
        borderRadius: {
            radius: fixedSpacing.sm, // 6px corner radius
            scope: 'stack',
            where: 'all'
        },
        height: 35,
        chartHeight: 80,
        spacing: fixedSpacing.md, // 8px between bars
        padding: fixedSpacing.md, // 12px
        horizontal: {
            legendPadding: fixedSpacing.xl // 16px,
        }
    },

    pie: {
        innerSize: '50%', // Donut style - lower = thicker ring
        borderWidth: 0,
        size: hdMonitor ? 350 : 293,
        paddingRight: fixedSpacing['3xl'], // 24px
        activeSliceColor: colors.primary600,
        activeBorderSize: 2,
        spacing: [10, 10, 10, 10]
    }
});

export default chartTheme;
